import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { createRole, fetchRoles as fetchAllRoles, fetchPermissions } from '../../store/slices/rolesSlice';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'remixicon/fonts/remixicon.css';
import Swal from 'sweetalert2';
import { getApiErrorMessage } from "../../utils/apiError";

const AddRole = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { permissions = [] } = useSelector((state) => state.roles);
    const [formData, setFormData] = useState({ name: '', permissions: [] });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        dispatch(fetchPermissions());
    }, [dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handlePermissionToggle = (permissionName) => {
        setFormData((prev) => ({
            ...prev,
            permissions: prev.permissions.includes(permissionName)
                ? prev.permissions.filter((p) => p !== permissionName)
                : [...prev.permissions, permissionName],
        }));
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            Swal.fire('Missing Field', 'Role name is required', 'warning');
            return false;
        }

        if (!formData.permissions || formData.permissions.length === 0) {
            Swal.fire('No Permissions', 'Please select at least one permission', 'warning');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            await dispatch(createRole(formData)).unwrap();
            await dispatch(fetchAllRoles());
            navigate('/roles');
        } catch (err) {
            setFormErrors({ general: getApiErrorMessage(err, 'Failed to create role') });
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredPermissionsList = (permissions || []).filter((p) =>
        p.name.toLowerCase().replace(/-/g, ' ').includes(searchText.toLowerCase())
    );

    const groupedPermissions = filteredPermissionsList.reduce((acc, permission) => {
        const parts = permission.name.split("-");
        const action = parts.pop();                     // last part → manage/create/edit/delete
        const group = parts.join("-");                  // remaining → discharge-summary

        if (!acc[group]) acc[group] = [];

        acc[group].push({
            ...permission,
            action,                                     // store the action separately
        });

        return acc;
    }, {});

    // Sort the group names alphabetically
    const sortedGroupedEntries = Object.entries(groupedPermissions).sort(([a], [b]) =>
        a.localeCompare(b)
    );

    const isAllSelected = filteredPermissionsList.length > 0 &&
        filteredPermissionsList.every((p) => formData.permissions.includes(p.name));

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card mb-4">
                    <div className="card-header d-flex justify-content-center align-items-center">
                        <h4 className="mb-0">Add Role</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit} id="addRoleForm">
                            {formErrors.general && <div className="alert alert-danger">{formErrors.general}</div>}

                            {/* ===== Floating Role Name Input ===== */}
                            <div className="col-md-12 mb-4">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                        id="roleName"
                                        name="name"
                                        placeholder=""
                                        value={formData.name}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="roleName" className="text-muted">
                                        <i className="ri-user-star-line me-2"></i>Enter New Role Name
                                    </label>
                                </div>
                                {formErrors.name && (
                                    <div className="text-danger small mt-1">{formErrors.name}</div>
                                )}
                            </div>

                            {/* ===== Permissions Header ===== */}
                            <div className="mb-4 d-flex flex-wrap justify-content-between align-items-center gap-3 border-bottom pb-3">
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <h6 className="mb-0 badge bg-light text-primary py-2 px-3">
                                        <i className="ri-lock-unlock-line me-2 text-primary"></i> Available Permissions
                                    </h6>
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Search permissions..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ width: "250px", borderRadius: "20px", paddingLeft: "35px" }}
                                        />
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                    </div>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="selectAllPermissions"
                                        checked={isAllSelected}
                                        onChange={() => {
                                            if (isAllSelected) {
                                                const filteredNames = filteredPermissionsList.map((p) => p.name);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    permissions: prev.permissions.filter((name) => !filteredNames.includes(name)),
                                                }));
                                            } else {
                                                const filteredNames = filteredPermissionsList.map((p) => p.name);
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    permissions: [...new Set([...prev.permissions, ...filteredNames])],
                                                }));
                                            }
                                        }}
                                    />
                                    <label className="form-check-label small fw-medium" htmlFor="selectAllPermissions">
                                        Select All
                                    </label>
                                </div>
                            </div>

                            {/* ===== Permission Cards ===== */}
                            {sortedGroupedEntries.length > 0 ? (
                                sortedGroupedEntries.map(([groupName, groupPerms]) => (
                                    <div key={groupName} className="mb-4">
                                        <h6 className="text-uppercase text-primary border-bottom pb-2 mb-3">
                                            <i className="ri-folder-2-line me-2"></i>
                                            {groupName.replaceAll('-', ' ')}
                                        </h6>
                                        <div className="row g-3">
                                            {groupPerms.map((permission) => {
                                                const isChecked = formData.permissions.includes(permission.name);
                                                return (
                                                    <div key={permission.id} className="col-md-4 col-lg-3">
                                                        <div
                                                            className={`card h-100 border transition-all ${isChecked ? 'border-primary bg-light' : 'border-light'}`}
                                                            style={{
                                                                transition: 'all 0.2s ease-in-out',
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => handlePermissionToggle(permission.name)}
                                                        >
                                                            <div className="card-body p-3">
                                                                <div className="d-flex align-items-center mb-2">
                                                                    <input
                                                                        className="form-check-input me-2"
                                                                        type="checkbox"
                                                                        id={`perm-${permission.id}`}
                                                                        checked={isChecked}
                                                                        onChange={() => handlePermissionToggle(permission.name)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    />
                                                                    <label
                                                                        className="form-check-label fw-semibold text-capitalize mb-0 flex-grow-1"
                                                                        htmlFor={`perm-${permission.id}`}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {permission.action}
                                                                    </label>
                                                                    <span
                                                                        className={`badge ${isChecked ? 'bg-primary' : 'bg-light text-secondary border'} small`}
                                                                    >
                                                                        {isChecked ? 'Enabled' : 'Disabled'}
                                                                    </span>
                                                                </div>
                                                                <p className="small text-muted mb-0">
                                                                    Manage access for this feature or module.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center py-5">
                                    <i className="ri-inbox-line fs-1 text-muted mb-3"></i>
                                    <p className="text-muted mb-0">No permissions found matching search</p>
                                </div>
                            )}

                            {/* ===== Submit Button ===== */}
                            <div className="row mt-5 pt-3 border-top">
                                <div className="col-1 pe-0">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100 btn-sm"
                                        onClick={() => navigate('/roles')}
                                    >
                                        <i className="ri-arrow-left-line me-1"></i>Back
                                    </button>
                                </div>
                                <div className="col-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 btn-sm sticky-form-submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Save
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line me-1"></i>Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddRole;